import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    TimesheetProjectRowExpansionContextActions,
    TimesheetProjectRowExpansionContextValue,
} from "features/timesheet/contexts/TimesheetProjectRowExpansionContext";
import { add, differenceInCalendarDays } from "date-fns";

import EmptyState from "shared/components/data-display/tables/EmptyState";
import Loading from "shared/components/feedback/loading/Loading";
import Row from "features/timesheet/timesheet-grid/Row";
import SessionContext from "app/contexts/SessionContext";
import Snackbar from "shared/components/feedback/notification/Snackbar";
import TimesheetGridContext from "features/timesheet/contexts/TimesheetGridContext";
import VStack from "shared/components/layout/stack/VStack";
import WeekNavigatorContext from "shared/contexts/WeekNavigatorContext";
import { getDateFnsFormatDate } from "shared/components/date-controls/week-navigator/WeekNavigatorMain";
import moment from "moment";
import styled from "styled-components";
import useTimeOffApi from "../time-off/querys/useTimeOff";
import useTimesheetGrid from "features/timesheet/timesheet-grid/useTimesheetGrid";
import useTimesheetGridTimeOff from "./useTimesheetGridTimeOff";

const MemoizedEmployeeRow = React.memo(Row);

export const LoadingWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-content: center;
    background-color: white;
`;

const PTO_LABELS = new Set(["PTO", "FTO"]);
export const TIME_OFF_ROW_ID = "TimeOff";
const NON_CLIENT_ROW_ID = "NonClientRelatedActivities";
const TOOLTIP_READONLY_HOURS_ALLOWED =
    'To edit your request, go"\n"to your Time Off page';
const TOOLTIP_READONLY_HOURS_NOT_ALLOWED =
    "To edit your request, contact\ntalentsupport@rga.com";

// If we have at least one holiday for the week, we create the project holiday
const setTimeOffHolidaysData = (holidaysDetails, timeOffDetails) => {
    if (holidaysDetails?.Holidays.length > 0) {
        const holidayProject = {
            JobCode: holidaysDetails.JobCode,
            Id: holidaysDetails.JobId,
            HalfDay: false,
            Name: holidaysDetails.Name,
            isHoliday: true,
        };
        timeOffDetails.push(holidayProject);
    }
};

const updateModifiedHolidays = (holidays, hours) => {
    if (holidays) {
        let newHolidays = [];
        holidays?.Holidays?.forEach((holiday) => {
            if (hours.length > 0) {
                const holidayDay = moment(holiday.Date).format("YYYY-MM-DD");

                // we look for the same day and job id to see if the user has modified the holiday
                const modifiedHoliday = hours.find((x) => {
                    const day = moment(x.timeDay).format("YYYY-MM-DD");
                    return (
                        x.jobId === holidays.JobId &&
                        moment(day).isSame(holidayDay)
                    );
                });

                if (modifiedHoliday) {
                    holiday.timeSpan = modifiedHoliday.timeSpan;
                }
            }
            newHolidays.push(holiday);
        });
        return newHolidays;
    }
};

const timeOffsToShow = (projectsDetails, projectHoursArr, futureWeek) => {
    return projectsDetails?.filter((detail) => {
        // if current or past and "show always on timehseet" enabled then we show it
        if (!futureWeek && detail.ShowAlwaysOnTimesheet) {
            return true;
        }

        // if it's holiday we always show it
        if (detail?.hasOwnProperty("HalfDay")) {
            return true;
        }

        // get the hours related to that time off/holiday project
        const allHours = projectHoursArr
            ?.filter((x) => x.projectId === detail?.LegacyJobId)
            ?.map((x) => x.hours)
            ?.flat();

        const hours = allHours?.filter(
            (x) => x.oooReasonId === detail?.ReasonId
        );

        // if there are hours then we show the time off project
        return hours?.some((x) => x.Id || x.isOOO);
    });
};

// if we are in a future week, we show only time off rows that have hours on it
// if we are in a current or past week, we show only time off rows that have hours on it plus the ones with "show always on timesheet" enabled
const filterTimeOffRows = (projects, projectHoursArr, isFutureWeek) => {
    let noTimeOffToshow = false;

    projects.forEach((project) => {
        if (project?.isTimeOffRow) {
            project.details = timeOffsToShow(
                project.details,
                projectHoursArr,
                isFutureWeek
            );
            noTimeOffToshow = project.details.length === 0;
        }
    });

    if (noTimeOffToshow) {
        return projects.filter((x) => !x.isTimeOffRow);
    }

    return projects;
};

const setHoursTimeOffFlag = (projects, projectHoursArr) => {
    const timeOffProjects = projects.find((x) => x.isTimeOffRow);
    const timeoffJobsIds = timeOffProjects?.details
        ?.map((x) => x.LegacyJobId)
        .filter(Boolean);
    if (timeoffJobsIds) {
        projectHoursArr?.forEach((pHrs) => {
            pHrs.isTimeOff = timeoffJobsIds.includes(pHrs.projectId);
        });
    }
    return projectHoursArr;
};

const updateTimeOffReadOnlyHours = (
    projectHoursArr,
    editableTimeOffJobisIds,
    isFutureWeek,
    isOneMonthBack
) => {
    projectHoursArr?.forEach((pHrs) => {
        if (pHrs.isTimeOff) {
            const isTimeOffEditableForUser = editableTimeOffJobisIds.includes(
                pHrs.projectId
            );
            pHrs?.hours?.forEach((hour) => {
                const oldTimeOff =
                    isOneMonthBack && hour.hasOwnProperty("isOOO");
                const readOnly =
                    oldTimeOff ||
                    isFutureWeek ||
                    (!isFutureWeek && !isTimeOffEditableForUser);
                hour.reandOnly = readOnly;
                if (oldTimeOff) {
                    hour.tooltipText = TOOLTIP_READONLY_HOURS_NOT_ALLOWED;
                } else if (readOnly && hour.hasOwnProperty("jobId"))
                    hour.tooltipText = isTimeOffEditableForUser
                        ? TOOLTIP_READONLY_HOURS_ALLOWED
                        : TOOLTIP_READONLY_HOURS_NOT_ALLOWED;
            });
        }
    });

    return projectHoursArr;
};

const updateHolidaysReadOnlyHours = (projectHoursArr, isOneMonthBack) => {
    projectHoursArr?.forEach((pHrs) => {
        if (pHrs.isHoliday) {
            pHrs?.hours?.forEach((hour) => {
                let readOnly =
                    !hour.hasOwnProperty("timeSpan") || isOneMonthBack;
                hour.reandOnly = readOnly;
            });
        }
    });

    return projectHoursArr;
};

const updateAprovedRejectedReadOnlyHours = (projectHoursArr) => {
    projectHoursArr?.forEach((pHrs) => {
        if (!pHrs.isHoliday && !pHrs.isTimeOff) {
            const isApprovedOrRejectd = !!pHrs?.hours?.find(
                (hour) => hour.teApproved || hour.teRejected
            );
            pHrs?.hours?.forEach((hour) => {
                let readOnly = isApprovedOrRejectd;
                hour.reandOnly = readOnly;
            });
        }
    });
    return projectHoursArr;
};

const setReadOnlyProjectsHoursOutOfDate = (projectHoursArr, projects) => {
    const projectsDetails = projects.flatMap((p) => p.details);
    projectHoursArr?.forEach((pHrs) => {
        if (!pHrs.isHoliday && !pHrs.isTimeOff) {
            const project = projectsDetails.find(
                (p) => p.ProjectId === pHrs.projectId
            );
            if (project) {
                const projectStartDate = project.ProjectStartDate;
                const projectEndDate = project.ProjectEndDate;
                const startDate = moment(projectStartDate).format("YYYY-MM-DD");
                const endDate = moment(projectEndDate).format("YYYY-MM-DD");

                pHrs?.hours?.forEach((hour) => {
                    const day = moment(hour.timeDay).format("YYYY-MM-DD");
                    const isBefore = moment(day).isBefore(startDate);
                    const isAfter = moment(day).isAfter(endDate);
                    if (isBefore || isAfter) {
                        hour.reandOnly = true;
                    }
                });
            }
        }
    });
    return projectHoursArr;
};

const disableWeekendCells = (nullHoursArr) => {
    [5, 6].forEach((d) => {
        nullHoursArr[d] = {
            ...nullHoursArr[d],
            timeSpan: null,
            isWeekendCellDisable: true,
        };
    });
};

const matchesHoursEntry = (
    x,
    detailsData,
    detailsDataProjectId,
    ptoFtoReasonsList
) => {
    if (x.isOOO) {
        return (
            detailsData.hasOwnProperty("LegacyJobId") &&
            detailsData.LegacyJobId === x.jobId &&
            x.oooReasonId === detailsData.ReasonId
        );
    }
    return (
        x.jobId === detailsDataProjectId ||
        detailsData.ReasonId === x.jobId ||
        (PTO_LABELS.has(detailsData.LegacyJobCode) &&
            ptoFtoReasonsList.some((s) => s.ReasonId === x.jobId))
    );
};

const buildProjectHoursEntry = (
    detailsData,
    detailsDataProjectId,
    hours,
    nullHoursArr,
    isHoliday,
    holidayContext,
    updaters
) => {
    const { holidaysData, newHoursData } = holidayContext;
    const { updateWeeksDataForProjectAndTimeOff, updateWeeksDataForHolidays } =
        updaters;
    if (hours?.length > 0 && !isHoliday) {
        const newHourArr = [...nullHoursArr];
        updateWeeksDataForProjectAndTimeOff(
            hours,
            newHourArr,
            detailsData?.hasOwnProperty("ReasonId")
        );
        const obj = {
            projectId: detailsDataProjectId,
            isHoliday: false,
            hours: newHourArr,
        };
        if (detailsData?.hasOwnProperty("ReasonId")) {
            obj.ReasonId = detailsData.ReasonId;
        }
        return obj;
    }
    if (isHoliday) {
        const newHolidaysHourArr = [...nullHoursArr];
        // We need to update the holidays in case the user did some updates on it
        const holidaysUpdated = updateModifiedHolidays(
            holidaysData?.data,
            newHoursData
        );
        updateWeeksDataForHolidays(holidaysUpdated, newHolidaysHourArr);
        return {
            projectId: detailsData?.Id,
            isHoliday,
            hours: newHolidaysHourArr,
        };
    }
    // update project hours with default hours data
    const obj = {
        projectId: detailsDataProjectId,
        isHoliday: false,
        hours: nullHoursArr,
    };
    if (detailsData?.hasOwnProperty("ReasonId")) {
        obj.ReasonId = detailsData.ReasonId;
    }
    return obj;
};

const buildProjectHoursArr = (
    projects,
    hoursData,
    ptoFtoReasonsList,
    holidaysData,
    getInitialHoursArray,
    updateWeeksDataForProjectAndTimeOff,
    updateWeeksDataForHolidays
) => {
    const projectHoursArr = [];
    const newHoursData = [...(hoursData?.data ?? [])];
    for (const p of projects ?? []) {
        for (const detailsData of p?.details ?? []) {
            // id of project, ooo and holiday respectively
            const detailsDataProjectId =
                detailsData?.ProjectId ||
                detailsData?.LegacyJobId ||
                detailsData?.Id;
            /* second condition added since we need FTO, PTO in one row
            and when added timeoff request then in jobId getting ReasonId not LegacyJobId */
            const hours = newHoursData?.filter((x) =>
                matchesHoursEntry(
                    x,
                    detailsData,
                    detailsDataProjectId,
                    ptoFtoReasonsList
                )
            );
            const isHoliday = detailsData?.hasOwnProperty("isHoliday");
            const nullHoursArr = [];
            getInitialHoursArray(nullHoursArr);
            // disable weekend cell for timeoff data with null value
            if (detailsData?.hasOwnProperty("LegacyJobId")) {
                disableWeekendCells(nullHoursArr);
            }
            projectHoursArr.push(
                buildProjectHoursEntry(
                    detailsData,
                    detailsDataProjectId,
                    hours,
                    nullHoursArr,
                    isHoliday,
                    { holidaysData, newHoursData },
                    {
                        updateWeeksDataForProjectAndTimeOff,
                        updateWeeksDataForHolidays,
                    }
                )
            );
        }
    }
    return projectHoursArr;
};

const Grid = () => {
    const session = useContext(SessionContext);

    const { useAssignments, useDepartmentsAssignments, useHoursTimeEntries } =
        useTimesheetGrid();

    const {
        isWeekChange,
        setIsWeekChange,
        selectedTimesheetStartDate,
        pendingTimesheetCalendarDate,
    } = useContext(WeekNavigatorContext);

    const { openAllProjectRows } = useContext(
        TimesheetProjectRowExpansionContextActions
    );

    const { allProjectRowsAreOpen } = useContext(
        TimesheetProjectRowExpansionContextValue
    );

    const {
        useGetEditableReasonsForTimesheet,
        useGetPendingHRTimeOffByDate,
        useGetApprovedTimeOffByDate,
        useGetUsedAllotmentByYear,
    } = useTimeOffApi();

    const {
        clientAndProjectDetails,
        setClientAndProjectDetails,
        projectHours,
        setProjectHours,
        weekColumnDayHoursLimitExceed,
        setDisableSubmitTimesheetButton,
        setPendingHRTimeOffInWeek,
        setApprovedTimeOffInWeek,
        setHolidays,
        setDisableGridUpdate,
        setUsedAllotment,
        setAllClientIds,
    } = useContext(TimesheetGridContext);

    const { useHolidaysTimesheet, useOooReasonsTimesheet } =
        useTimesheetGridTimeOff();

    const [showSnackbar, setShowSnackbar] = useState(false);

    let fromDateVal = null;
    let untilDateVal = null;

    fromDateVal = selectedTimesheetStartDate ?? pendingTimesheetCalendarDate;

    fromDateVal = fromDateVal
        ? `${fromDateVal.year}-${fromDateVal.month}-${fromDateVal.day}`
        : null;

    const weekEndDate = fromDateVal
        ? selectedTimesheetStartDate.add({ days: 6 })
        : null;

    untilDateVal = fromDateVal
        ? `${weekEndDate.year}-${weekEndDate.month}-${weekEndDate.day}`
        : null;

    const requestData = {
        employeeId: session.legacyId,
        fromDate: fromDateVal,
    };

    const requestDepartmentsAssignmentsData = {
        departmentId: session.currentDepartmentId,
        officeId: session.currentOfficeId,
        agencyId: session.agencyId,
        employeeId: session.legacyId,
        fromDate: fromDateVal,
    };

    const requestHolidaysData = {
        officeId: session.currentLegacyOfficeId,
        fromDate: fromDateVal,
    };

    const reasonsEditablesForUser = useGetEditableReasonsForTimesheet({
        employeeId: session.legacyId,
        year: weekEndDate.year ?? new Date().getFullYear(),
    });

    const userData = useAssignments(requestData);
    const activitiesData = useDepartmentsAssignments(
        requestDepartmentsAssignmentsData
    );
    const hoursData = useHoursTimeEntries(requestData);
    const holidaysData = useHolidaysTimesheet(requestHolidaysData);
    const oooReasonsData = useOooReasonsTimesheet({
        fromDate: fromDateVal,
        agencyId: session.agencyId,
    });
    const pendingsHRTimeOffs = useGetPendingHRTimeOffByDate({
        from: fromDateVal,
        until: untilDateVal,
    });
    const approvedTimeOffs = useGetApprovedTimeOffByDate({
        from: fromDateVal,
        until: untilDateVal,
    });
    const usedAllotment = useGetUsedAllotmentByYear({
        userId: session.legacyId,
        year: weekEndDate.year ?? new Date().getFullYear(),
    });

    const flagRef = useRef(true);

    useEffect(() => {
        if (
            userData?.isFetching ||
            activitiesData?.isFetching ||
            hoursData?.isFetching ||
            oooReasonsData?.isFetching ||
            holidaysData?.isFetching ||
            usedAllotment?.isFetching
        ) {
            flagRef.current = true;
            setDisableSubmitTimesheetButton(true);
        }
    }, [
        userData,
        activitiesData,
        hoursData,
        oooReasonsData,
        holidaysData,
        usedAllotment,
        setDisableSubmitTimesheetButton,
    ]);

    useEffect(() => {
        const disabledGrid =
            hoursData?.isFetching ||
            pendingsHRTimeOffs?.isFetching ||
            approvedTimeOffs?.isFetching ||
            usedAllotment?.isFetching;

        setDisableGridUpdate(disabledGrid);
    }, [
        hoursData,
        pendingsHRTimeOffs,
        approvedTimeOffs,
        usedAllotment,
        setDisableGridUpdate,
    ]);

    const startDate = getDateFnsFormatDate(
        selectedTimesheetStartDate?.year,
        selectedTimesheetStartDate?.month,
        selectedTimesheetStartDate?.day
    );

    const getInitialHoursArray = useCallback(
        (nullHoursArr) => {
            // initial value for a week
            for (let i = 0; i < 7; i++) {
                nullHoursArr.push({
                    timeDay: add(startDate, { days: i }),
                });
            }
        },
        [startDate]
    );

    const expandAllRowsInitially = useCallback(() => {
        if (!allProjectRowsAreOpen) {
            openAllProjectRows();
        }
    }, [allProjectRowsAreOpen, openAllProjectRows]);

    const updateWeeksDataForHolidays = useCallback(
        (holidays, newHolidaysHourArr) => {
            holidays?.forEach((holiday) => {
                const indexOfHolidayCell = differenceInCalendarDays(
                    new Date(holiday.Date),
                    startDate
                );

                const isWeekendCell =
                    indexOfHolidayCell === 5 || indexOfHolidayCell === 6;

                let holidayTimeSpan = 0;

                // if we have a timeSpan it means that the user has modified the holiday
                if (
                    holiday.timeSpan !== undefined &&
                    holiday.timeSpan !== null
                ) {
                    holidayTimeSpan = holiday.timeSpan;
                } else {
                    holidayTimeSpan = holiday.HalfDay ? 4 : 8;
                }

                const newHoliday = {
                    ...holiday,
                    timeSpan: isWeekendCell ? null : holidayTimeSpan,
                    isWeekendCellDisable: isWeekendCell,
                };

                newHolidaysHourArr[indexOfHolidayCell] = newHoliday;
            });
        },
        [startDate]
    );

    const updateWeeksDataForProjectAndTimeOff = useCallback(
        (hoursDetails, newHourArr, isTimeOffDetails) => {
            hoursDetails.forEach((hour) => {
                const hourCellIndex = differenceInCalendarDays(
                    new Date(hour.timeDay),
                    startDate
                );

                const isWeekendCell =
                    hourCellIndex === 5 || hourCellIndex === 6;

                if (isWeekendCell && isTimeOffDetails) {
                    newHourArr[hourCellIndex] = {
                        ...hour,
                        timeSpan: null,
                        isWeekendCellDisable: true,
                    };
                } else {
                    newHourArr[hourCellIndex] = {
                        ...hour,
                        isWeekendCellDisable: false,
                    };
                }
            });
        },
        [startDate]
    );

    const getPtoFtoReasonsList = useCallback(() => {
        return oooReasonsData?.data?.filter((f) =>
            PTO_LABELS.has(f.LegacyJobCode)
        );
    }, [oooReasonsData]);

    const getUniqueOOOReasons = useCallback(() => {
        return oooReasonsData?.data;
    }, [oooReasonsData]);

    const isFutureWeek = useCallback(() => {
        let rv = false;
        if (startDate) {
            let selectDate = moment(startDate).format("YYYY-MM-DD");
            let currentWeek = moment().startOf("isoWeek").format("YYYY-MM-DD");
            rv = moment(selectDate).isAfter(currentWeek);
        }
        return rv;
    }, [startDate]);

    const isOneMonthOldWeek = useCallback(() => {
        let rv = false;
        if (startDate) {
            let currentWeek = moment().startOf("isoWeek").format("YYYY-MM-DD");
            const oneMonthBack = moment(currentWeek)
                .subtract(4, "W")
                .format("YYYY-MM-DD");
            let initialWeekDateOfSelected = moment(startDate)
                .startOf("isoWeek")
                .format("YYYY-MM-DD");
            rv = moment(oneMonthBack).isAfter(initialWeekDateOfSelected);
        }
        return rv;
    }, [startDate]);

    const getEditableJobsIdsForUser = useCallback(
        (projects) => {
            let jobsIds = [];
            const reasonsIds = reasonsEditablesForUser?.data.map(
                (x) => x.reasonId
            );
            projects?.forEach((project) => {
                if (project?.isTimeOffRow) {
                    project.details.forEach((timeOffJob) => {
                        if (
                            timeOffJob.hasOwnProperty("ReasonId") &&
                            reasonsIds.includes(timeOffJob.ReasonId)
                        ) {
                            jobsIds.push(timeOffJob.LegacyJobId);
                        }
                    });
                }
            });
            return jobsIds;
        },
        [reasonsEditablesForUser]
    );

    // Staff-only activities are filtered by the API based on the employee's
    // employment type in the selected week, so only empty groups are removed here.
    const filterEmptyProjects = useCallback(
        (projects) => projects.filter((p) => p.details.length !== 0),
        []
    );

    useEffect(() => {
        let projects = [];

        if (
            !userData?.isFetching &&
            !activitiesData?.isFetching &&
            !hoursData?.isFetching &&
            !oooReasonsData?.isFetching &&
            !holidaysData?.isFetching &&
            !reasonsEditablesForUser?.isLoading &&
            !pendingsHRTimeOffs?.isLoading &&
            !approvedTimeOffs?.isLoading &&
            !usedAllotment?.isLoading &&
            flagRef.current
        ) {
            setDisableSubmitTimesheetButton(false);
            expandAllRowsInitially();
            setShowSnackbar(false);
            setPendingHRTimeOffInWeek(pendingsHRTimeOffs?.data);
            setApprovedTimeOffInWeek(approvedTimeOffs?.data);
            setUsedAllotment(usedAllotment?.data);
            setHolidays(holidaysData?.data);

            const data = userData.data;
            const clientIds = [...new Set(data.map((d) => d.ClientId))];
            let projectIds = [];

            clientIds.forEach((d) => {
                const filteredData = data.filter((f) => f.ClientId === d);
                const rowObj = {
                    clientData: {
                        id: filteredData[0]?.ClientId,
                        name: filteredData[0]?.ClientName,
                    },
                    details: filteredData,
                };
                projects.push(rowObj);
                filteredData.map((p) => projectIds.push(p.ProjectId));
            });

            const nonClientData = activitiesData.data;
            const nonClientRowObj = {
                clientData: {
                    id: NON_CLIENT_ROW_ID,
                    name: "Non-Client-Related Activities",
                },
                details: nonClientData,
            };
            projects.push(nonClientRowObj);
            nonClientData.map((p) => projectIds.push(p.ProjectId));

            let timeOffDetails = [];

            // holidays data
            setTimeOffHolidaysData(holidaysData?.data, timeOffDetails);

            // filter the unique ooo reasons respective to office
            const timeOffDetailsNew = getUniqueOOOReasons();

            if (timeOffDetailsNew?.length > 0) {
                timeOffDetails = [...timeOffDetails, ...timeOffDetailsNew];
            }

            timeOffDetails.sort((a, b) => a.DisplayOrder - b.DisplayOrder);

            // timeoff details in projects
            if (!session.freelancer && !session.contractor) {
                if (timeOffDetails.length > 0) {
                    const timeOffObj = {
                        clientData: {
                            id: TIME_OFF_ROW_ID,
                            name: "Time Off",
                        },
                        details: timeOffDetails,
                        isTimeOffRow: true,
                    };
                    projects.push(timeOffObj);
                }
            }

            const ptoFtoReasonsList = getPtoFtoReasonsList();

            // set project hours weeks data
            let projectHoursArr = buildProjectHoursArr(
                projects,
                hoursData,
                ptoFtoReasonsList,
                holidaysData,
                getInitialHoursArray,
                updateWeeksDataForProjectAndTimeOff,
                updateWeeksDataForHolidays
            );

            const isFutureWeekSelected = isFutureWeek();
            const isOneMonthBack = isOneMonthOldWeek();
            const editableTimeOffJobisIds = getEditableJobsIdsForUser(projects);

            projectHoursArr = setHoursTimeOffFlag(projects, projectHoursArr);
            projectHoursArr = updateTimeOffReadOnlyHours(
                projectHoursArr,
                editableTimeOffJobisIds,
                isFutureWeekSelected,
                isOneMonthBack
            );
            projectHoursArr = updateHolidaysReadOnlyHours(
                projectHoursArr,
                isOneMonthBack
            );
            setProjectHours(projectHoursArr);
            projectHoursArr =
                updateAprovedRejectedReadOnlyHours(projectHoursArr);
            projectHoursArr = setReadOnlyProjectsHoursOutOfDate(
                projectHoursArr,
                projects
            );

            setProjectHours(projectHoursArr);

            projects = filterTimeOffRows(
                projects,
                projectHoursArr,
                isFutureWeekSelected
            );
            projects = filterEmptyProjects(projects);
            setAllClientIds(projects.map((p) => p.clientData.id));
            setClientAndProjectDetails(projects);

            flagRef.current = false;
            isWeekChange && setIsWeekChange(false);
        }
    }, [
        userData,
        activitiesData,
        hoursData,
        oooReasonsData,
        holidaysData,
        pendingsHRTimeOffs,
        approvedTimeOffs,
        usedAllotment,
        reasonsEditablesForUser,
        isWeekChange,
        setIsWeekChange,
        setDisableSubmitTimesheetButton,
        setPendingHRTimeOffInWeek,
        setApprovedTimeOffInWeek,
        setUsedAllotment,
        setHolidays,
        setProjectHours,
        setClientAndProjectDetails,
        setAllClientIds,
        expandAllRowsInitially,
        getPtoFtoReasonsList,
        getUniqueOOOReasons,
        getInitialHoursArray,
        updateWeeksDataForHolidays,
        updateWeeksDataForProjectAndTimeOff,
        getEditableJobsIdsForUser,
        filterEmptyProjects,
        isFutureWeek,
        isOneMonthOldWeek,
        session.freelancer,
        session.contractor,
    ]);

    useEffect(() => {
        setShowSnackbar(weekColumnDayHoursLimitExceed);
        setDisableSubmitTimesheetButton(weekColumnDayHoursLimitExceed);
    }, [
        clientAndProjectDetails,
        weekColumnDayHoursLimitExceed,
        setDisableSubmitTimesheetButton,
    ]);

    const handleOnCloseSnackbar = () => {
        setShowSnackbar(false);
        setDisableSubmitTimesheetButton(false);
    };

    if (isWeekChange) {
        return (
            <LoadingWrapper>
                <Loading text="Loading entries..." />
            </LoadingWrapper>
        );
    }

    if (hoursData?.isError) {
        return <>Can't load hours data</>;
    }

    if (userData?.isError) {
        return <>Can't load user data</>;
    }

    if (oooReasonsData?.isError) {
        return <>Can't load Out of Office reasons data</>;
    }

    if (holidaysData?.isError) {
        return <>Can't load holidays data</>;
    }

    if (reasonsEditablesForUser?.isError) {
        return <>Can't load reasons editables for user data</>;
    }

    return (
        <>
            {userData?.data?.length > 0 || oooReasonsData?.data?.length > 0 ? (
                <VStack spacing="Two">
                    <div>
                        {clientAndProjectDetails?.map((item) => (
                            <MemoizedEmployeeRow
                                key={item.clientData?.id}
                                hoursData={hoursData}
                                data={item}
                                projectHours={projectHours}
                                oooReasonsData={oooReasonsData}
                            />
                        ))}
                    </div>
                    {showSnackbar && (
                        <Snackbar
                            icon={true}
                            onClose={handleOnCloseSnackbar}
                            feedbackIcon="warning"
                            message="More than 24 hours have been entered to a day. Please reduce the hours for that day."
                        />
                    )}
                </VStack>
            ) : (
                <EmptyState message="There doesn't appear to be anything here." />
            )}
        </>
    );
};

Grid.propTypes = {};

export default Grid;
